import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  Users, UserPlus, User, Crown, Heart, Baby, UserCog,
  Loader2, Trash2, Edit, Mail, Phone, Calendar, 
  Activity, ChevronRight, Shield, AlertTriangle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RELATIONSHIP_OPTIONS = [
  { value: 'spouse', label: 'Cónyuge', icon: Heart },
  { value: 'child', label: 'Hijo/a', icon: Baby },
  { value: 'parent', label: 'Padre/Madre', icon: User },
  { value: 'sibling', label: 'Hermano/a', icon: Users },
  { value: 'other', label: 'Otro', icon: UserCog }
];

const FamilyMembersManager = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [familyData, setFamilyData] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    date_of_birth: '',
    phone: '',
    address: '',
    emergency_contact: '',
    emergency_phone: '',
    medical_notes: '',
    relationship: '',
    create_user_account: false
  });

  useEffect(() => {
    fetchFamilyMembers();
  }, []);

  const fetchFamilyMembers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/family-members/my-family`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFamilyData(response.data);
    } catch (error) {
      console.error('Error fetching family members:', error);
      if (error.response?.status === 404) {
        // No membership yet
        setFamilyData(null);
      } else {
        toast.error('No se pudo cargar la información familiar');
      }
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      date_of_birth: '',
      phone: '',
      address: '',
      emergency_contact: '',
      emergency_phone: '',
      medical_notes: '',
      relationship: '',
      create_user_account: false
    });
  };

  const handleAddDependent = async () => {
    if (!formData.full_name || !formData.date_of_birth || !formData.relationship) {
      toast.error('Por favor complete los campos requeridos');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/family-members/add-dependent`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${formData.full_name} agregado a la membresía familiar`);
      setShowAddDialog(false);
      resetForm();
      fetchFamilyMembers();
    } catch (error) {
      console.error('Error adding dependent:', error);
      toast.error(error.response?.data?.detail || 'No se pudo agregar el dependiente');
    } finally {
      setSaving(false);
    }
  };

  const handleEditDependent = async () => {
    if (!selectedMember) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/family-members/dependent/${selectedMember.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Información actualizada correctamente');
      setShowEditDialog(false);
      setSelectedMember(null);
      resetForm();
      fetchFamilyMembers();
    } catch (error) {
      console.error('Error updating dependent:', error);
      toast.error(error.response?.data?.detail || 'No se pudo actualizar la información');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDependent = async () => {
    if (!selectedMember) return;

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/family-members/dependent/${selectedMember.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`${selectedMember.full_name} removido de la membresía`);
      setShowDeleteDialog(false);
      setSelectedMember(null);
      fetchFamilyMembers();
    } catch (error) {
      console.error('Error removing dependent:', error);
      toast.error(error.response?.data?.detail || 'No se pudo remover el dependiente');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (member) => {
    setSelectedMember(member);
    setFormData({
      full_name: member.full_name || '',
      email: member.email || '',
      date_of_birth: member.date_of_birth || '',
      phone: member.phone || '',
      address: '',
      emergency_contact: '',
      emergency_phone: '',
      medical_notes: '',
      relationship: member.relationship || '',
      create_user_account: false
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (member) => {
    setSelectedMember(member);
    setShowDeleteDialog(true);
  };

  const getRelationshipLabel = (value) => {
    const rel = RELATIONSHIP_OPTIONS.find(r => r.value === value);
    return rel ? rel.label : value;
  };

  const getRelationshipIcon = (value) => {
    const rel = RELATIONSHIP_OPTIONS.find(r => r.value === value);
    const Icon = rel?.icon || User;
    return <Icon className="h-4 w-4" />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <Loader2 className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!familyData) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Sin Membresía Activa</h3>
            <p className="text-gray-500 mt-2">
              Necesita tener una membresía activa para gestionar miembros familiares.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const isPlanFamily = familyData.max_members > 1;

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-teal-100 rounded-full">
                <Users className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">
                  {familyData.plan_name}
                </h2>
                <p className="text-sm text-gray-600">
                  {isPlanFamily ? (
                    <>
                      Plan Familiar · {familyData.current_members} de {familyData.max_members} miembros
                    </>
                  ) : (
                    'Plan Individual'
                  )}
                </p>
              </div>
            </div>
            
            {isPlanFamily && familyData.can_add_more && (
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Agregar Familiar
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Agregar Familiar a la Membresía</DialogTitle>
                    <DialogDescription>
                      Complete la información del nuevo miembro familiar. 
                      Espacios disponibles: {familyData.slots_available}
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="full_name">Nombre Completo *</Label>
                        <Input
                          id="full_name"
                          value={formData.full_name}
                          onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                          placeholder="Nombre completo del familiar"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="relationship">Parentesco *</Label>
                        <Select
                          value={formData.relationship}
                          onValueChange={(value) => setFormData({...formData, relationship: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Seleccionar..." />
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_OPTIONS.map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                <div className="flex items-center gap-2">
                                  <option.icon className="h-4 w-4" />
                                  {option.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div>
                        <Label htmlFor="date_of_birth">Fecha de Nacimiento *</Label>
                        <Input
                          id="date_of_birth"
                          type="date"
                          value={formData.date_of_birth}
                          onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="email">Correo Electrónico</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="correo@ejemplo.com"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="phone">Teléfono</Label>
                        <Input
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => setFormData({...formData, phone: e.target.value})}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label htmlFor="address">Dirección</Label>
                        <Input
                          id="address"
                          value={formData.address}
                          onChange={(e) => setFormData({...formData, address: e.target.value})}
                          placeholder="Calle, número, colonia, ciudad"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="emergency_contact">Contacto de Emergencia</Label>
                        <Input
                          id="emergency_contact"
                          value={formData.emergency_contact}
                          onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                          placeholder="Nombre del contacto"
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="emergency_phone">Tel. Emergencia</Label>
                        <Input
                          id="emergency_phone"
                          value={formData.emergency_phone}
                          onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                          placeholder="(555) 123-4567"
                        />
                      </div>
                      
                      <div className="col-span-2">
                        <Label htmlFor="medical_notes">Notas Médicas</Label>
                        <Textarea
                          id="medical_notes"
                          value={formData.medical_notes}
                          onChange={(e) => setFormData({...formData, medical_notes: e.target.value})}
                          placeholder="Alergias, condiciones médicas, medicamentos, etc."
                          rows={3}
                        />
                      </div>
                      
                      {formData.email && (
                        <div className="col-span-2 flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div>
                            <Label className="text-blue-900">Crear Cuenta de Usuario</Label>
                            <p className="text-sm text-blue-700">
                              Permitir que este familiar inicie sesión y agende sus propias citas
                            </p>
                          </div>
                          <Switch
                            checked={formData.create_user_account}
                            onCheckedChange={(checked) => setFormData({...formData, create_user_account: checked})}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => {setShowAddDialog(false); resetForm();}}>
                      Cancelar
                    </Button>
                    <Button onClick={handleAddDependent} disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                      Agregar Familiar
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )}
          </div>
          
          {/* Progress bar for family slots */}
          {isPlanFamily && (
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Miembros</span>
                <span>{familyData.current_members} / {familyData.max_members}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-teal-600 h-2 rounded-full transition-all"
                  style={{ width: `${(familyData.current_members / familyData.max_members) * 100}%` }}
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Not a family plan message */}
      {!isPlanFamily && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Plan Individual</p>
                <p className="text-sm text-amber-800 mt-1">
                  Su plan actual es individual y solo permite 1 miembro. 
                  Para agregar familiares, considere actualizar a un plan familiar.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Family Members List */}
      <div className="grid gap-4">
        {familyData.members.map((member) => (
          <Card key={member.id} className={member.is_primary ? 'border-teal-300 bg-teal-50/30' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-full ${member.is_primary ? 'bg-teal-100' : 'bg-gray-100'}`}>
                    {member.is_primary ? (
                      <Crown className="h-5 w-5 text-teal-600" />
                    ) : (
                      getRelationshipIcon(member.relationship)
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900">{member.full_name}</h3>
                      {member.is_primary && (
                        <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100">
                          Titular
                        </Badge>
                      )}
                      {member.has_user_account && !member.is_primary && (
                        <Badge variant="outline" className="text-blue-600 border-blue-200">
                          <Shield className="h-3 w-3 mr-1" />
                          Cuenta Activa
                        </Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-gray-500">
                      {!member.is_primary && (
                        <span className="flex items-center gap-1">
                          {getRelationshipIcon(member.relationship)}
                          {getRelationshipLabel(member.relationship)}
                        </span>
                      )}
                      {member.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {member.email}
                        </span>
                      )}
                      {member.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          {member.phone}
                        </span>
                      )}
                      {member.date_of_birth && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {member.date_of_birth}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Badge 
                    variant={member.membership_status === 'active' ? 'default' : 'secondary'}
                    className={member.membership_status === 'active' ? 'bg-green-100 text-green-700' : ''}
                  >
                    {member.membership_status === 'active' ? 'Activo' : member.membership_status}
                  </Badge>
                  
                  {!member.is_primary && (
                    <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => openEditDialog(member)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDeleteDialog(member)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  
                  <Button variant="ghost" size="sm" className="text-teal-600">
                    <Activity className="h-4 w-4 mr-1" />
                    Servicios
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Editar Información</DialogTitle>
            <DialogDescription>
              Actualice la información del familiar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="edit_full_name">Nombre Completo</Label>
                <Input
                  id="edit_full_name"
                  value={formData.full_name}
                  onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit_relationship">Parentesco</Label>
                <Select
                  value={formData.relationship}
                  onValueChange={(value) => setFormData({...formData, relationship: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {RELATIONSHIP_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="edit_date_of_birth">Fecha de Nacimiento</Label>
                <Input
                  id="edit_date_of_birth"
                  type="date"
                  value={formData.date_of_birth}
                  onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit_email">Correo Electrónico</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="edit_phone">Teléfono</Label>
                <Input
                  id="edit_phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => {setShowEditDialog(false); setSelectedMember(null); resetForm();}}>
              Cancelar
            </Button>
            <Button onClick={handleEditDependent} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Remover familiar de la membresía?</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedMember && (
                <>
                  ¿Está seguro que desea remover a <strong>{selectedMember.full_name}</strong> de la membresía familiar?
                  <br /><br />
                  Esta acción desactivará su membresía y acceso a servicios. El registro se conservará para historial médico.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setSelectedMember(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteDependent}
              className="bg-red-600 hover:bg-red-700"
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FamilyMembersManager;
